import Model, { attr, hasMany } from '@ember-data/model';
import { cached, tracked } from '@glimmer/tracking';
import type { ModelRegistry } from 'ember-data/types/registries/model';

export type ConnectionStatus =
  | 'active'
  | 'inactive'
  | 'onboarding'
  | 'pending_contract';

interface IChannelConnectionData {
  code?: string;
  legal_entity_id?: string;
}

export type AccessLevel = 'full' | 'read_only';

export type ChannelType = 'airbnb' | 'booking_dot_com';

export default class ChannelConnectionModel extends Model {
  @attr('string') name!: string;
  @attr('string') type!: ChannelType;
  @attr('string') status!: ConnectionStatus;
  @attr('string') accessLevel!: AccessLevel;
  @attr('object') data!: IChannelConnectionData;

  @hasMany('channel-listing', { async: false, inverse: 'channelConnection' })
  channelListings!: ModelRegistry['channel-listing'][];

  @hasMany('channel-link', { async: false, inverse: 'channelConnection' })
  channelLinks!: ModelRegistry['channel-link'][];

  // handling state on the page, because we want to know which items are expanded and collapsed
  @tracked isCollapsed = true;

  @cached
  get isOnboarding(): boolean {
    return this.status === 'onboarding';
  }

  @cached
  get isPendingContract(): boolean {
    return this.status === 'pending_contract';
  }

  @cached
  get isReadOnly(): boolean {
    return this.accessLevel === 'read_only';
  }

  // Names arrive as "Name [email]".
  @cached
  get displayName(): string {
    return (this.name ?? '').replace(/ \[[^\]]*\]$/, '');
  }

  @cached
  get email(): string | undefined {
    return /\[([^\]]*)\]$/.exec(this.name ?? '')?.[1];
  }

  @cached
  get originalId(): string {
    return this.id.split('_').pop() ?? this.id;
  }

  @cached
  get hasListingsToImport(): boolean {
    return (
      (this.channelLinks?.length ?? 0) < (this.channelListings?.length ?? 0)
    );
  }
}

declare module 'ember-data/types/registries/model' {
  interface ModelRegistry {
    'channel-connection': ChannelConnectionModel;
  }
}
